import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CategoryMultiSelect from '../../components/CategoryMultiSelect';
import ContactMethodsEditor from '../../components/ContactMethodsEditor';
import DataTable from '../../components/table/DataTable';
import ActionMenu from '../../components/ui/ActionMenu';
import FormField from '../../components/ui/FormField';
import FormModal from '../../components/ui/FormModal';
import Modal from '../../components/ui/Modal';
import SearchBar from '../../components/ui/SearchBar';
import StatusMessage from '../../components/ui/StatusMessage';
import { useAuth } from '../../context/AuthContext';
import {
  createSupplier,
  getCategories,
  getSuppliers,
  permanentlyDeleteSupplier,
  restoreSupplier,
  softDeleteSupplier,
} from '../../services/firestoreService';
import {
  buildLookupMap,
  getDisplayValues,
  normalizeIdArray,
} from '../../utils/collectionUtils';
import { getPrimaryContactMethod } from '../../utils/contactMethods';
import {
  buildSupplierFormPayload,
  createEmptySupplierForm,
  getFieldValue,
  setFieldValue,
  supplierFormSections,
  validateSupplierForm,
} from './supplierFormUtils';

export default function SuppliersList({ viewMode = 'active' }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [restoreId, setRestoreId] = useState(null);
  const [deletePermanentId, setDeletePermanentId] = useState(null);
  const [formData, setFormData] = useState(createEmptySupplierForm);
  const [categoryMap, setCategoryMap] = useState({});
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  useEffect(() => {
    const fetchSuppliersAndCategories = async () => {
      setLoading(true);
      setPageError('');

      try {
        const [suppliersData, categories] = await Promise.all([
          getSuppliers(),
          getCategories(),
        ]);

        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
        setCategoryMap(buildLookupMap(categories, 'name'));
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        setSuppliers([]);
        setCategoryMap({});
        setPageError('Failed to load suppliers.');
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliersAndCategories();
  }, []);

  const getCategoryNames = (categoryIds) =>
    getDisplayValues(categoryIds, categoryMap);

  const handleOpenDeleteModal = (supplier) => {
    setSelectedSupplier(supplier);
    setIsModalOpen(true);
  };

  const handleOpenAddModal = () => {
    setFormData(createEmptySupplierForm());
    setFormError('');
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    if (isSubmitting) {
      return;
    }

    setIsAddModalOpen(false);
    setFormData(createEmptySupplierForm());
    setFormError('');
  };

  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setSelectedSupplier(null);
  };

  const updateSupplierStatus = (id, updates) => {
    setSuppliers((prev) =>
      prev.map((supplier) =>
        supplier.id === id ? { ...supplier, ...updates } : supplier
      )
    );
  };

  const handleConfirmDelete = async () => {
    try {
      if (!selectedSupplier?.id) {
        return;
      }

      await softDeleteSupplier(selectedSupplier.id);

      updateSupplierStatus(selectedSupplier.id, {
        isDeleted: true,
        deletedAt: new Date(),
      });

      setPageMessage('Supplier moved to Trash.');
      setPageError('');
      handleCloseDeleteModal();
    } catch (error) {
      console.error('DELETE ERROR:', error);
      setPageError('Failed to delete supplier.');
    }
  };

  const handleRestore = async () => {
    try {
      if (!restoreId) {
        return;
      }

      await restoreSupplier(restoreId);

      updateSupplierStatus(restoreId, {
        isDeleted: false,
        deletedAt: null,
      });

      setPageMessage('Supplier restored.');
      setPageError('');
      setRestoreId(null);
    } catch (error) {
      console.error('RESTORE ERROR:', error);
      setPageError('Failed to restore supplier.');
    }
  };

  const handlePermanentDelete = async (id) => {
    try {
      await permanentlyDeleteSupplier(id);
      setSuppliers((prev) => prev.filter((supplier) => supplier.id !== id));
      setPageMessage('Supplier permanently deleted.');
      setPageError('');
    } catch (error) {
      console.error('PERMANENT DELETE ERROR:', error);
      setPageError('Failed to permanently delete supplier.');
    }
  };

  const handleAddSupplier = async (event) => {
    event.preventDefault();

    const nextFormError = validateSupplierForm(formData);

    if (nextFormError) {
      setFormError(nextFormError);
      return;
    }

    const payload = {
      ...buildSupplierFormPayload(formData),
      isDeleted: false,
      deletedAt: null,
    };

    setFormError('');
    setIsSubmitting(true);

    try {
      const supplier = await createSupplier(payload);

      setSuppliers((prev) => [...prev, supplier]);
      setPageMessage('Supplier created.');
      setPageError('');
      handleCloseAddModal();
    } catch (error) {
      console.error('ADD SUPPLIER ERROR:', error);
      setFormError('Failed to add supplier.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const normalizedSuppliers = suppliers.map((supplier) => ({
    ...supplier,
    categories: normalizeIdArray(supplier.categories),
  }));

  const filteredSuppliers = normalizedSuppliers
    .filter((supplier) => {
      if (viewMode === 'active') {
        return supplier.isDeleted !== true;
      }

      if (viewMode === 'deleted') {
        return supplier.isDeleted === true;
      }

      return true;
    })
    .filter((supplier) => {
      const matchesSearch = (supplier.companyName || '')
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase());
      const matchesCategory =
        selectedCategories.length === 0 ||
        supplier.categories?.some((categoryId) =>
          selectedCategories.includes(categoryId)
        );

      return matchesSearch && matchesCategory;
    });

  const columns = [
    { key: 'company', label: 'Company' },
    { key: 'phone', label: 'Primary Contact' },
    { key: 'email', label: 'Email' },
    { key: 'categories', label: 'Categories' },
  ];

  if (isAdmin) {
    columns.push({ key: 'actions', label: '' });
  }

  const rowStyle = {
    cursor: 'default',
  };

  const companyCellStyle = {
    color: '#0f172a',
  };

  const actionsCellStyle = {
    width: 72,
    textAlign: 'right',
  };

  return (
    <div className="page page--compact-list">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">Suppliers</h1>
          <p className="page-subtitle">
            Manage supplier records and quickly review contact details.
          </p>
        </div>

        {isAdmin && (
          <div className="page-header__actions">
            <button
              type="button"
              className="button button-primary"
              onClick={handleOpenAddModal}
            >
              + Add Supplier
            </button>
          </div>
        )}
      </div>

      <StatusMessage tone="success" message={pageMessage} />
      <StatusMessage tone="error" message={pageError} />

      <section className="section">
        <div className="controls-bar">
          <div className="controls-bar__search">
            <SearchBar
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search suppliers by company name..."
            />
          </div>
          <div className="controls-bar__filter">
            <CategoryMultiSelect
              value={selectedCategories}
              onChange={setSelectedCategories}
              compact
            />
          </div>

          <div className="toggle-group">
            <button
              type="button"
              onClick={() => navigate('/suppliers')}
              className={viewMode === 'active' ? 'active' : ''}
            >
              Active
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => navigate('/suppliers/trash')}
                className={viewMode === 'deleted' ? 'active' : ''}
              >
                Trash
              </button>
            )}
          </div>
        </div>

        {loading ? <p className="page-subtitle">Loading suppliers...</p> : null}

        {!loading ? (
          <div className="table-wrapper list-table-wrapper">
            <DataTable
              columns={columns}
              data={filteredSuppliers}
              emptyMessage="No suppliers found."
              renderRow={(supplier) => (
                <tr
                  key={supplier.id}
                  style={rowStyle}
                >
                  <td style={companyCellStyle}>
                    <button
                      type="button"
                      className="clickable-name"
                      onClick={() => navigate(`/supplier/${supplier.id}`)}
                    >
                      {supplier.companyName || '-'}
                    </button>
                  </td>
                  <td>{getPrimaryContactMethod(supplier.contactMethods)?.number || '-'}</td>
                  <td>{supplier.companyEmail || '-'}</td>
                  <td>
                    <div className="tag-list">
                      {getCategoryNames(supplier.categories).length > 0
                        ? getCategoryNames(supplier.categories).map((categoryName) => (
                          <span
                            key={categoryName}
                            className="tag"
                          >
                            {categoryName}
                          </span>
                        ))
                        : '-'}
                    </div>
                  </td>
                  {isAdmin && (
                    <td
                      style={actionsCellStyle}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {viewMode === 'deleted' ? (
                        <div
                          className="row-actions trash-row-actions"
                        >
                          <button
                            type="button"
                            onClick={() => setRestoreId(supplier.id)}
                            className="btn btn-success"
                            style={{ padding: '8px 12px' }}
                          >
                            Restore
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletePermanentId(supplier.id)}
                            className="btn btn-danger"
                            style={{ padding: '8px 12px' }}
                          >
                            Delete Permanently
                          </button>
                        </div>
                      ) : (
                        <ActionMenu
                          onEdit={() => navigate(`/supplier/${supplier.id}/edit`)}
                          onDelete={() => handleOpenDeleteModal(supplier)}
                        />
                      )}
                    </td>
                  )}
                </tr>
              )}
            />
          </div>
        ) : null}
      </section>

      {isAdmin && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Delete Supplier"
          description={`Are you sure you want to delete ${
            selectedSupplier?.companyName || 'this supplier'
          }? You can restore it later from Trash.`}
        />
      )}

      {isAdmin && (
        <FormModal
          isOpen={isAddModalOpen}
          onClose={handleCloseAddModal}
          onSubmit={handleAddSupplier}
          title="Add Supplier"
          submitLabel={isSubmitting ? 'Saving...' : 'Save Supplier'}
          submitDisabled={isSubmitting}
          cancelDisabled={isSubmitting}
          errorMessage={formError}
        >
          {supplierFormSections.map((section) => (
            <fieldset key={section.title} className="form-section">
              <legend>{section.title}</legend>
              <div className="form-grid">
                {section.fields.map((field) => (
                  <FormField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    required={field.required}
                    value={getFieldValue(formData, field.name)}
                    onChange={(value) => {
                      setFormData((prev) => setFieldValue(prev, field.name, value));
                      if (formError) {
                        setFormError('');
                      }
                    }}
                  />
                ))}
              </div>
            </fieldset>
          ))}
          <ContactMethodsEditor
            value={formData.contactMethods}
            onChange={(contactMethods) => {
              setFormData((prev) => ({ ...prev, contactMethods }));
              if (formError) {
                setFormError('');
              }
            }}
            disabled={isSubmitting}
            idPrefix="supplier-add-contact-method"
          />
          <div className="form-group">
            <label>Categories</label>
            <CategoryMultiSelect
              value={normalizeIdArray(formData.categories)}
              onChange={(newCategories) =>
                setFormData((prev) => ({
                  ...prev,
                  categories: normalizeIdArray(newCategories),
                }))
              }
            />
          </div>
        </FormModal>
      )}

      {isAdmin && (
        <Modal
          isOpen={Boolean(restoreId)}
          onClose={() => setRestoreId(null)}
          onConfirm={handleRestore}
          title="Restore Supplier"
          description="Are you sure you want to restore this supplier?"
        />
      )}

      {isAdmin && (
        <Modal
          isOpen={Boolean(deletePermanentId)}
          onClose={() => setDeletePermanentId(null)}
          onConfirm={async () => {
            await handlePermanentDelete(deletePermanentId);
            setDeletePermanentId(null);
          }}
          title="Delete Permanently"
          description="This action cannot be undone. Are you sure you want to permanently delete this supplier?"
        />
      )}
    </div>
  );
}
